/**
 * iOS keyboard for the mobile assistant chat.
 *
 * Capacitor's default (`resize: native`) waits for the keyboard to finish,
 * then shrinks the WebView in one jump, so a bottom-docked composer lags the
 * keyboard and snaps. In chat mode the WebView stays full height instead and
 * `chatKeyboardHeight` drives the dock's `bottom` from `keyboardWillShow`,
 * which fires as the keyboard starts moving. The dock and the transcript
 * padding transition over the same duration and curve, so they travel
 * together. The auth screen uses the same idea (nativeAuthKeyboard.ts).
 *
 * Listeners register once when the first chat shell mounts (so the first
 * keyboardWillShow is never missed) and stay until the last one unmounts: two
 * shells can be mounted at once (Home's chat under the phone AssistantPanel).
 * Chat mode is per shell too: it's on while any shell asks for it, so one
 * shell's teardown can't switch it off under another. The resize-mode switch
 * only happens while chat mode is on, so other inputs keep the default behavior.
 *
 * Only the dock's own textarea (`[data-chat-dock-input]`) gets this treatment.
 * A field anywhere else while the chat is up (a draft in the workspace sheet,
 * a form in an overlay) gets the normal native resize for that keyboard
 * session: the WebView shrinks around it, and the chat's resize mode comes back
 * once that keyboard has gone (`keyboardDidHide`, after the WebView grew back).
 */
import { ref } from 'vue'
import type { PluginListenerHandle } from '@capacitor/core'
import { isIosNative } from '@/utils/platform'
import {
    overlayViewport,
    restoreViewport,
    setChatKeyboardActive,
} from '@/utils/nativeAuthKeyboard'
import { noteNativeResizeMode } from '@/utils/nativeResizeMode'

/** Keyboard height in px while chat mode is on and the keyboard is up. */
export const chatKeyboardHeight = ref(0)

/**
 * When the native file menu is opening, keep the dock at the last keyboard
 * height. iOS anchors that popover to the tap, so if the plus slides down
 * with the keyboard the menu is left hanging at the old spot.
 */
let dockHeld = false

let chatActive = false
/** The shells that want chat mode on; it's on while this isn't empty. */
const activeOwners = new Set<symbol>()
/** A keyboard that belongs to some other field (not the dock) is up, with native resize. */
let foreignKeyboard = false
let listeners: PluginListenerHandle[] = []
/** Mounted shells sharing the listeners (the first registers them, the last removes them). */
let users = 0

/** The phone chat's dock textarea has focus (the only field the chat keyboard mode is for). */
export function dockHasFocus(el: Element | null = typeof document !== 'undefined' ? document.activeElement : null): boolean {
    return el instanceof HTMLElement && el.matches('[data-chat-dock-input]')
}
let initToken = 0

/** Fired whenever the keyboard starts moving, so the transcript can stay pinned. */
export const CHAT_VIEWPORT_EVENT = 'lh-chat-viewport'

function notifyViewportChange() {
    window.dispatchEvent(new CustomEvent(CHAT_VIEWPORT_EVENT))
}

export async function initChatKeyboard() {
    users += 1
    if (!isIosNative() || users > 1) return
    const token = ++initToken
    const { Keyboard, KeyboardResize } = await import('@capacitor/keyboard')

    const show = await Keyboard.addListener('keyboardWillShow', (info) => {
        if (!chatActive) return
        if (foreignKeyboard) return
        if (!dockHasFocus()) {
            // Another field: let the WebView resize for it as it would anywhere else in the app.
            foreignKeyboard = true
            chatKeyboardHeight.value = 0
            noteNativeResizeMode('native')
            void Keyboard.setResizeMode({ mode: KeyboardResize.Native })
            void Keyboard.setScroll({ isDisabled: false })
            return
        }
        dockHeld = false
        // The plugin schedules its WebView snap ~keyboard-duration later.
        // Switching to none now turns that snap into a no-op.
        noteNativeResizeMode('none')
        void Keyboard.setResizeMode({ mode: KeyboardResize.None })
        chatKeyboardHeight.value = Math.round(info.keyboardHeight || 0)
        notifyViewportChange()
    })
    const hide = await Keyboard.addListener('keyboardWillHide', () => {
        if (foreignKeyboard) return
        if (dockHeld) return
        if (chatKeyboardHeight.value === 0) return
        chatKeyboardHeight.value = 0
        notifyViewportChange()
    })
    // The WebView has grown back by now (the plugin restores it right after willHide): chat mode resumes.
    const didHide = await Keyboard.addListener('keyboardDidHide', () => {
        if (!foreignKeyboard) return
        foreignKeyboard = false
        if (chatActive) void setChatKeyboardActive(true)
    })

    if (token !== initToken) {
        await show.remove()
        await hide.remove()
        await didHide.remove()
        return
    }
    listeners = [show, hide, didHide]
}

/** A shell is going away: drop its chat mode, and the listeners once no shell is left. */
export async function disposeChatKeyboard(owner: symbol) {
    setChatKeyboardMode(owner, false)
    users = Math.max(0, users - 1)
    if (users > 0) return
    initToken += 1
    const pending = listeners
    listeners = []
    await Promise.all(pending.map((l) => l.remove()))
}

/** Freeze the dock at the current keyboard offset (native file menu). */
export function holdChatDock() {
    if (chatKeyboardHeight.value > 0) dockHeld = true
}

/** Let the dock fall back to the edge after the file menu is gone. */
export function releaseChatDock() {
    if (!dockHeld) return
    dockHeld = false
    if (chatKeyboardHeight.value === 0) return
    chatKeyboardHeight.value = 0
    notifyViewportChange()
}

/**
 * Turn chat keyboard mode on or off for one shell (`owner`); it's on while any
 * shell has it on. Call it synchronously when the user focuses the composer:
 * `keyboardWillShow` arrives right after the focus.
 */
export function setChatKeyboardMode(owner: symbol, on: boolean) {
    if (!isIosNative()) return
    if (on) activeOwners.add(owner)
    else activeOwners.delete(owner)
    const active = activeOwners.size > 0
    if (chatActive === active) return
    chatActive = active
    if (active) {
        overlayViewport()
    } else {
        dockHeld = false
        foreignKeyboard = false
        restoreViewport()
        chatKeyboardHeight.value = 0
    }
    void setChatKeyboardActive(active)
}
